import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiRequest } from "../apiService/allapi";
import { errorAlert, successAlert } from "../apiService/sweetAlert";
import {
  addToGuestCart,
  dispatchCartUpdate,
  isLoggedIn,
} from "../helper/cartUtils";

const NewArrival = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [wishlistProducts, setWishlistProducts] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const productsPerPage = 8;

  const getProducts = async (cid, scid, sscid) => {
    try {
      setLoading(true);
      const res = await apiRequest(
        `/Product/product/list/${cid}/${scid}`,
        "GET",
      );

      let productData = [];
      if (Array.isArray(res.data)) {
        productData = res.data;
      } else if (Array.isArray(res.data?.data)) {
        productData = res.data.data;
      } else if (Array.isArray(res.data?.data?.data)) {
        productData = res.data.data.data;
      }
      setProducts(productData);
      setCurrentPage(1); // Reset to first page on new fetch
    } catch (error) {
      console.log("Product Fetch Error:", error);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getProducts(0, 0, 0);
  }, []);

  useEffect(() => {
    getWishlistItems();
  }, []);

  // Pagination helpers
  const totalPages = Math.ceil(products.length / productsPerPage);
  const displayedProducts = products.slice(
    (currentPage - 1) * productsPerPage,
    currentPage * productsPerPage,
  );

  const goToPrevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const goToPage = (page) => {
    setCurrentPage(page);
  };

  const addToCart = async (product) => {
    const dispatchUpdate = () => {
      dispatchCartUpdate();
      window.dispatchEvent(new CustomEvent("cartUpdated"));
    };

    const token = localStorage.getItem("token");
    const pid = product.id;

    if (!token) {
      const cart = JSON.parse(localStorage.getItem("guestCart")) || [];
      if (!product.isStock) {
        errorAlert("Out of Stock");
        return;
      }

      const guestItem = {
        pid: product.id,
        productName: product.productName,
        sellingPrice: product.sellingPrice,
        imG1: product.imgurl,
        qty: 1,
      };

      addToGuestCart(guestItem);
      successAlert("Product added to cart");
      dispatchUpdate();
      return;
    }

    try {
      const payload = { pid: product.id, qty: 1 };
      const res = await apiRequest("/Cart/add-to-cart", "POST", payload, {
        Authorization: `Bearer ${token}`,
      });
      if (res?.data?.status === true) {
        successAlert("Product added to cart");
        dispatchUpdate();
      } else {
        errorAlert(res.data.message);
      }
    } catch (error) {
      console.log(error);
      errorAlert("Failed to add to cart");
    }
  };

  const addToWishlist = async (pid) => {
    try {
      const token = localStorage.getItem("token");
      const res = await apiRequest(
        "/Wishlist/AddToggleWishlist",
        "POST",
        { productId: pid },
        { Authorization: `Bearer ${token}` },
      );

      if (res?.data?.success === true || res?.data?.status === true) {
        successAlert(res.data.message);
        window.dispatchEvent(new Event("wishlistUpdated"));

        setWishlistProducts((prev) => {
          const updated = new Set(prev);
          if (updated.has(pid)) {
            updated.delete(pid);
          } else {
            updated.add(pid);
          }
          return updated;
        });
      } else {
        errorAlert(res?.data?.message || "Failed");
      }
    } catch (error) {
      console.log("Wishlist Error:", error);
      errorAlert("Something went wrong");
    }
  };

  const getWishlistItems = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      const res = await apiRequest("/Wishlist/getToggle", "GET", null, {
        Authorization: `Bearer ${token}`,
      });

      if (res?.data?.status === true) {
        const wishlistPids = new Set(
          (res.data.data || []).map((item) => item.pid),
        );
        setWishlistProducts(wishlistPids);
      }
    } catch (error) {
      console.log("Load wishlist error:", error);
    }
  };

  if (loading) {
    return <div className="text-center py-20">Loading recent products...</div>;
  }

  return (
    <section
      className="new-arrival pb-80"
      style={{ background: "#f4f4f4", padding: "50px 0px",margin:"0 0 50px 0"  }}
    >
      <div className="container container-lg">
        <div className="section-heading">
          <div className="flex-between flex-wrap gap-8">
            <h5 className="mb-0">Recent Products</h5>
            <div className="flex-align mr-point gap-16">
              <Link
                to="/shop"
                className=" fw-medium text-gray-700 hover-text-main-600 hover-text-decoration-underline"
                style={{ fontSize: "18px" }}
              >
                View All Deals
              </Link>
            </div>
          </div>
        </div>

        {/* Products Grid - 4 products per row like e-commerce */}
        <div className="row g-4 mb-32">
          {displayedProducts.map((product) => (
            <div key={product.id} className="col-lg-3 col-md-6">
              <div className="product-card h-100 p-16 border border-dark shadow bg-white hover-border-main-600 rounded-16 position-relative transition-2">
                {isLoggedIn() && (
                  <button
                    onClick={() => addToWishlist(product.id)}
                    className="wishlist-btn position-absolute top-0 end-0 m-12 rounded-circle flex-center"
                    style={{
                      width: "40px",
                      height: "40px",
                      border: "none",
                      zIndex: 10,
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill={
                        wishlistProducts.has(product.id) ? "#ef4444" : "none"
                      }
                      stroke={
                        wishlistProducts.has(product.id)
                          ? "#ef4444"
                          : "currentColor"
                      }
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="feather feather-heart transition-colors duration-200"
                    >
                      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
                    </svg>
                  </button>
                )}
                <Link
                  to={`/product-details/${product.id}`}
                  className="product-card__thumb flex-center rounded-8 position-relative shadow" style={{overflow:"hidden"}}
                >
                  <div className="product-img">
                  <img
                    src={`${process.env.REACT_APP_IMAGE_BASE_URL}${product.imgurl}`}
                    alt={product.productName}
                    style={{maxWidth:"288px"}}
                    
                  />
                  </div>
                </Link>

                <div
                  className="product-card__content "
                  style={{width: "100%" }}
                >
                  <div className="flex-align mb-20 mt-16 gap-6">
                    <span className="text-xs fw-medium text-gray-500">
                      {product.ratingAverage || 0}
                    </span>
                    <span className="text-15 fw-medium text-warning-600 d-flex">
                      <i className="ph-fill ph-star" />
                    </span>
                    <span className="text-xs fw-medium text-gray-500">
                      ({product.totalRating || 0})
                    </span>
                  </div>

                  <h6 className="title text-lg fw-semibold mt-12 mb-8">
                    <Link
                      to={`/product-details/${product.id}`}
                      className="link text-line-2"
                    >
                      {product.productName}
                    </Link>
                  </h6>

                  <div className="product-card__price my-20">
                    <span className="text-gray-400 text-md fw-semibold text-decoration-line-through">
                      ₹{Number(product.mrp).toFixed(2)}
                    </span>
                    <span className="text-heading text-md fw-semibold">
                      ₹{Number(product.sellingPrice).toFixed(2)}
                    </span>
                  </div>

                  {product.isStock ? (
                    <button
                      onClick={() => addToCart(product)}
                      className="border border-black product-card__cart btn bg-white text-heading hover-bg-main-600 hover-text-white py-11 px-24 rounded-pill flex-center gap-8 fw-medium w-full border-1"
                    >
                      Add To Cart <i className="ph ph-shopping-cart" />
                    </button>
                  ) : (
                    <span className="text-danger fw-medium">Out of Stock</span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex-center flex-wrap gap-8">
            <button
              onClick={goToPrevPage}
              disabled={currentPage === 1}
              className="page-link h-48 w-48 flex-center text-lg rounded-8 fw-medium text-neutral-600 border border-gray-100 hover-bg-main-600 hover-text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <i className="ph-bold ph-arrow-left" />
            </button>

            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              return (
                <button
                  key={pageNum}
                  onClick={() => goToPage(pageNum)}
                  className={`page-link h-48 w-48 flex-center text-md rounded-8 fw-medium border border-gray-100 ${
                    currentPage === pageNum
                      ? "text-white bg-main-600 border-main-600"
                      : "text-neutral-600 hover-bg-main-600 hover-text-white"
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}

            <button
              onClick={goToNextPage}
              disabled={currentPage === totalPages}
              className="page-link h-48 w-48 flex-center text-lg rounded-8 fw-medium text-neutral-600 border border-gray-100 hover-bg-main-600 hover-text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <i className="ph-bold ph-arrow-right" />
            </button>
            <span className="text-sm text-neutral-600">
              Page {currentPage} of {totalPages}
            </span>
          </div>
        )}
      </div>
    </section>
  );
};

export default NewArrival;
